import React from 'react';
import { Button, CUIText, Icon, IconNames } from '@dotcom/crown-ui';
import styles from '../toolbar-action-menu.module.scss';

export type ToolbarHeaderProps = {
  backLabel: string;
  closeLabel: string;
  onBack: () => void;
  onClose: () => void;
  showBack?: boolean;
  title?: string;
  backTestId?: string;
  closeTestId?: string;
};

/**
 * Single-row header for a `ToolbarActionMenu` panel.
 *
 * Layout is a 3-column CSS grid: `[left | center | right]`.
 * - `left` slot: back button when `showBack`, otherwise empty.
 * - `center` slot: `title` text (always shown when provided).
 * - `right` slot: close (X) button when `!showBack`, otherwise empty.
 *
 * Rationale: keeping title + trailing action on the same row (single-row
 * chrome) matches the standard modal/drawer header pattern. Removing the
 * X in sub-panels avoids duplicated exits (back already handles the
 * navigation) and lets `Escape` serve as the direct-close affordance.
 */
export const ToolbarHeader = ({
  backLabel,
  closeLabel,
  onBack,
  onClose,
  showBack,
  title,
  backTestId,
  closeTestId,
}: ToolbarHeaderProps): React.ReactElement => (
  <div className={styles['toolbar-header']}>
    <div className={styles['toolbar-header-slot-left']}>
      {showBack && (
        <Button
          variant="secondary"
          size="icon"
          aria-label={backLabel}
          className={styles['header-icon-button']}
          testId={backTestId}
          onClick={onBack}
        >
          <Icon name={IconNames.ArrowsCaretUp} size="16" className={styles['header-back-icon']} />
        </Button>
      )}
    </div>
    <div className={styles['toolbar-header-slot-center']}>
      {title && (
        <CUIText variant="h4" className={styles['toolbar-header-title']}>
          {title}
        </CUIText>
      )}
    </div>
    <div className={styles['toolbar-header-slot-right']}>
      {!showBack && (
        <Button
          variant="secondary"
          size="icon"
          aria-label={closeLabel}
          className={styles['header-icon-button']}
          testId={closeTestId}
          onClick={onClose}
        >
          <Icon name={IconNames.Menuclose} size="16" />
        </Button>
      )}
    </div>
  </div>
);
